/**
 * PhotoMosaic — the Rails-era hero+2 photo mosaic with a full gallery
 * modal (estates-enrichment PR-4). Since D14 the photos are Google Places
 * photos minted for THIS view (see `EstateGallery`); the author credit and
 * the Google Maps link ride every photo (Google requirement).
 *
 * Each mosaic tile is a `relative` box holding TWO siblings: the dialog
 * trigger `<button>` (the image, filling the tile) and the credit overlay
 * (`EstatePhotoCredit`, positioned over the button's corner). The credit's
 * links are never inside the button — an `<a>` in a `<button>` is invalid
 * HTML, and the button would swallow the click on the policy-required Maps
 * link. Clicking the link follows it; clicking anywhere else on the tile
 * opens the gallery.
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { EstatePhotoData } from '@/shared/lib/types/estates';
import { plainText } from '@letsgethome/types/branded';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@letsgethome/ui/dialog';

import { EstatePhoto, EstatePhotoCredit, EstatePhotoImage } from './EstatePhoto';

type Props = {
  photos: EstatePhotoData[];
  /** Resolved estate name for alt text. */
  name: string;
};

/**
 * Side-column photo count of the hero+2 mosaic (M/L debt item L6: the bare
 * `slice(0, 2)` literal, named). The static `sm:grid-rows-2` Tailwind class
 * below mirrors this count — Tailwind's JIT needs the literal class, so the
 * two are welded by this comment + the adjacent usage, not by interpolation.
 */
const MOSAIC_SIDE_PHOTOS = 2;

export function PhotoMosaic({ photos, name }: Props) {
  const t = useTranslations('estates.detail.gallery');
  const tDetail = useTranslations('estates.detail');
  if (photos.length === 0) {
    return null;
  }
  const [hero, ...rest] = photos;
  const side = rest.slice(0, MOSAIC_SIDE_PHOTOS);

  return (
    <Dialog>
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {hero && (
            <MosaicTile
              photo={hero}
              alt={plainText(name)}
              className="relative col-span-1 aspect-[4/3] overflow-hidden rounded-xl sm:col-span-2 sm:aspect-auto sm:min-h-[380px]"
            />
          )}
          {side.length > 0 && (
            <div className="hidden grid-rows-2 gap-2 sm:grid">
              {side.map((photo, i) => (
                <MosaicTile
                  key={i}
                  photo={photo}
                  alt={plainText(tDetail('photoAlt', { name, index: i + 2 }))}
                  className="relative overflow-hidden rounded-xl"
                />
              ))}
            </div>
          )}
        </div>
        {photos.length > 1 && (
          <DialogTrigger asChild>
            <button
              type="button"
              className="mt-2 text-sm font-medium text-success-text hover:underline"
            >
              {t('viewAll', { count: photos.length })}
            </button>
          </DialogTrigger>
        )}
      </div>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogTitle className="sr-only">{plainText(name)}</DialogTitle>
        <div className="flex flex-col gap-3">
          {photos.map((photo, i) => (
            <div key={i} className="relative w-full overflow-hidden rounded-lg">
              <EstatePhoto
                photo={photo}
                alt={plainText(tDetail('photoAlt', { name, index: i + 1 }))}
                className="h-auto w-full object-contain"
              />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One mosaic tile: a `relative` box holding the dialog trigger (the image,
 * filling the tile) and, as its SIBLING, the credit overlay — never inside the
 * button (see the module doc).
 */
function MosaicTile({
  photo,
  alt,
  className,
}: {
  photo: EstatePhotoData;
  alt: string;
  className: string;
}) {
  return (
    <div className={className}>
      <DialogTrigger asChild>
        <button type="button" className="absolute inset-0 overflow-hidden">
          <EstatePhotoImage
            photo={photo}
            alt={alt}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 hover:scale-105"
          />
        </button>
      </DialogTrigger>
      <EstatePhotoCredit photo={photo} />
    </div>
  );
}
